import { useState } from 'react';
import Card from 'components/card';
import { MdPerson, MdSearch, MdGroup, MdTrendingUp, MdTouchApp, MdDelete } from 'react-icons/md';
import AlertPopup from 'components/popup/AlertPopup';
import ConfirmPopup from 'components/popup/ConfirmPopup';

interface ActiveAssignment {
  id: string;
  mentor: {
    id: string;
    name: string;
    skills: string[];
  };
  mentees: {
    id: string;
    name: string;
    interests: string[];
  }[];
  type: '1:1' | '1:many';
  status: 'active';
  assignmentMethod: 'manual' | 'algorithm';
}

const ActiveAssignments = () => {
  const [searchTerm, setSearchTerm] = useState('');

  // Popup states
  const [alertConfig, setAlertConfig] = useState({
    isOpen: false,
    message: '',
    title: 'Alert',
    variant: 'info' as 'success' | 'warning' | 'info' | 'error',
  });
  const [confirmConfig, setConfirmConfig] = useState({
    isOpen: false,
    message: '',
    title: 'Confirm',
    onConfirm: () => {},
  });

  // Handle remove assignment
  const handleRemoveAssignment = (assignmentId: string) => {
    setConfirmConfig({
      isOpen: true,
      message: 'Are you sure you want to remove this assignment?',
      title: 'Remove Assignment',
      onConfirm: () => {
        setActiveAssignments(prev => prev.filter(assignment => assignment.id !== assignmentId));
      },
    });
  };

  // Handle clear all manual assignments
  const handleClearAllManualAssignments = () => {
    const manualAssignments = activeAssignments.filter(assignment => assignment.assignmentMethod === 'manual');
    if (manualAssignments.length === 0) {
      setAlertConfig({
        isOpen: true,
        message: 'No manual assignments to clear.',
        title: 'No Assignments',
        variant: 'info',
      });
      return;
    }

    setConfirmConfig({
      isOpen: true,
      message: `Are you sure you want to remove all ${manualAssignments.length} manual assignments? This action cannot be undone.`,
      title: 'Clear Manual Assignments',
      onConfirm: () => {
        setActiveAssignments(prev => prev.filter(assignment => assignment.assignmentMethod !== 'manual'));
      },
    });
  };

  // Mock active assignment data
  const [activeAssignments, setActiveAssignments] = useState<ActiveAssignment[]>([
    {
      id: '1',
      mentor: {
        id: '1',
        name: 'Dr. Sarah Chen',
        skills: ['Machine Learning', 'Data Science', 'Python']
      },
      mentees: [
        {
          id: '4',
          name: 'John Smith',
          interests: ['Machine Learning', 'AI']
        },
        {
          id: '5',
          name: 'Lisa Wang',
          interests: ['Data Science', 'Python']
        }
      ],
      type: '1:many',
      status: 'active',
      assignmentMethod: 'algorithm'
    },
    {
      id: '2',
      mentor: {
        id: '2',
        name: 'Jennifer Kim',
        skills: ['UX Design', 'Product Management', 'Figma']
      },
      mentees: [
        {
          id: '6',
          name: 'Sophie Chen',
          interests: ['UX Design', 'User Research']
        }
      ],
      type: '1:1',
      status: 'active',
      assignmentMethod: 'manual'
    },
    {
      id: '3',
      mentor: {
        id: '7',
        name: 'Prof. Maria Garcia',
        skills: ['Organic Chemistry', 'Research Methods']
      },
      mentees: [
        {
          id: '8',
          name: 'Alex Rodriguez',
          interests: ['Organic Chemistry', 'Research']
        },
        {
          id: '9',
          name: 'Emma Thompson',
          interests: ['Laboratory Techniques']
        }
      ],
      type: '1:many',
      status: 'active',
      assignmentMethod: 'algorithm'
    },
    {
      id: '4',
      mentor: {
        id: '9',
        name: 'Dr. Ahmed Khan',
        skills: ['Theoretical Physics', 'Quantum Mechanics']
      },
      mentees: [
        {
          id: '11',
          name: 'Rachel Green',
          interests: ['Quantum Physics', 'Theoretical Research']
        }
      ],
      type: '1:1',
      status: 'active',
      assignmentMethod: 'manual'
    }
  ]);

  // Filter assignments - only show manual assignments
  const filteredAssignments = activeAssignments.filter(assignment => {
    // Only show manual assignments
    if (assignment.assignmentMethod !== 'manual') return false;
    
    const matchesSearch = 
      assignment.mentor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assignment.mentees.some(mentee => 
        mentee.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    
    return matchesSearch;
  });


  return (
    <div className="mt-3 h-full w-full">
      <Card extra="w-full pb-10 p-6 h-full">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/10">
              <MdTrendingUp className="h-6 w-6 text-green-500" />
            </div>
            <div>
              <h4 className="text-xl font-bold text-navy-700 dark:text-white">
                Active Assignments
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                View all currently active manual mentor-mentee assignments ({filteredAssignments.length} active)
              </p>
            </div>
          </div>

          {/* Search and Clear Button */}
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search mentors or mentees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-gray-600 dark:bg-navy-800 dark:text-white"
              />
            </div>

            {/* Clear All Manual Assignments Button */}
            <button
              onClick={handleClearAllManualAssignments}
              className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg text-sm font-medium hover:bg-gray-600 transition-colors"
            >
              <MdDelete className="h-4 w-4" />
              Clear All Manual Assignments
            </button>
          </div>
        </div>


        {/* Active Assignments List */}
        <div className="space-y-4">
          {filteredAssignments.length === 0 ? (
            <div className="text-center py-12">
              <MdGroup className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-semibold text-gray-600 dark:text-gray-400 mb-2">
                No active assignments found
              </h3>
              <p className="text-gray-500 dark:text-gray-500">
                {searchTerm
                  ? 'Try adjusting your search'
                  : 'No manual assignments are currently active'
                }
              </p>
            </div>
          ) : (
            filteredAssignments.map((assignment) => (
              <div
                key={assignment.id}
                className="bg-white dark:bg-navy-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4 group"
              >
                <div className="flex items-center justify-between">
                  {/* Mentor Info */}
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                      <MdPerson className="h-5 w-5 text-gray-600" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-navy-700 dark:text-white">
                        {assignment.mentor.name}
                      </h5>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {assignment.mentor.skills.map((skill, index) => (
                          <span
                            key={index}
                            className="inline-flex px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Assignment Type and Method */}
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                      assignment.type === '1:1' 
                        ? 'bg-brand-100 text-brand-800 dark:bg-brand-900/20 dark:text-brand-300'
                        : 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300'
                    }`}>
                      {assignment.type === '1:1' ? 'One to One' : 'One to Many'}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
                      <MdTouchApp className="h-3 w-3" />
                      Manual
                    </span>
                    {/* Remove button */}
                    <button
                      onClick={() => handleRemoveAssignment(assignment.id)}
                      className="p-2 text-gray-400 hover:text-red-500 transition-all rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 opacity-0 group-hover:opacity-100"
                      title="Remove assignment"
                    >
                      <MdDelete className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Mentees */}
                <div className="mt-4 pl-14">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    Mentees ({assignment.mentees.length}):
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {assignment.mentees.map((mentee) => (
                      <div key={mentee.id} className="flex items-center gap-2 bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2">
                        <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                          <MdPerson className="h-3 w-3 text-gray-600" />
                        </div>
                        <span className="text-sm font-medium text-navy-700 dark:text-white">
                          {mentee.name}
                        </span>
                        <div className="flex flex-wrap gap-1">
                          {mentee.interests.map((interest, index) => (
                            <span
                              key={index}
                              className="inline-flex px-1 py-0.5 rounded text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300"
                            >
                              {interest}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>

      {/* Alert Popup */}
      <AlertPopup
        isOpen={alertConfig.isOpen}
        onClose={() => setAlertConfig({ ...alertConfig, isOpen: false })}
        title={alertConfig.title}
        message={alertConfig.message}
        variant={alertConfig.variant}
      />

      {/* Confirm Popup */}
      <ConfirmPopup
        isOpen={confirmConfig.isOpen}
        onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText="Confirm"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

export default ActiveAssignments;